import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UserInfo } from '@/api';

interface UserRoleDialogProps {
  open: boolean;
  user: UserInfo | null;
  onClose: () => void;
  onSave: (userType: number) => void;
}

const roles = [
  { value: 0, key: 'notActivated' },
  { value: 1, key: 'user' },
  { value: 2, key: 'admin' },
  { value: 3, key: 'frozen' },
];

export function UserRoleDialog({ open, user, onClose, onSave }: UserRoleDialogProps) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<number>(user?.userType ?? 1);

  // Update selected role when user changes
  if (user && selectedRole !== user.userType && !open) {
    setSelectedRole(user.userType);
  }

  const handleSave = () => {
    onSave(selectedRole);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.changeRole')}</DialogTitle>
      <DialogContent>
        {user && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        )}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>{t('user.role')}</InputLabel>
          <Select
            value={selectedRole}
            label={t('user.role')}
            onChange={(e) => setSelectedRole(e.target.value as number)}
          >
            {roles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {t(`roles.${role.key}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained">
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
